import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import BuildIcon from "@mui/icons-material/Build";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ChecklistIcon from "@mui/icons-material/Checklist";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import logo from "../../../OV-WarrantyMS.png";
import { useNavigate } from "react-router-dom";
import { auth, firestore } from "../../../firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getDocs, collection, query, where } from "firebase/firestore";

const OrganisationAdminNavBar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSections, setOpenSections] = useState({
    warranty: true,
    amc: true,
    contracts: true,
  });
  const navigate = useNavigate();

  const profileLogoUrl = tenantInfo?.organisationLogoUrl || tenantInfo?.institutionLogoUrl || "";

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Fetch user profile by email since doc ID may not be UID
          const q = query(
            collection(firestore, "UserProfileMaster"),
            where("email", "==", user.email)
          );
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            const data = userDoc.data();
            setUserProfile(data);
            if (data.tenantId) {
              const tenantDoc = await getDoc(doc(firestore, "TenantMaster", data.tenantId));
              if (tenantDoc.exists()) {
                setTenantInfo(tenantDoc.data());
              }
            }
          }
        } catch (error) {
          console.error("Error fetching user/tenant info:", error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setLogoLoadError(false);
  }, [profileLogoUrl]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    signOut(auth).then(() => {
      sessionStorage.removeItem("userRole");
      sessionStorage.removeItem("tenantId");
      navigate("/login");
    });
  };

  const handleDrawerNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleSectionToggle = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setDrawerOpen(open);
  };

  const getInitials = (name) => {
    if (!name) return "IA";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getDisplayRole = (role) => {
    if (["organisation_admin", "OU Admin"].includes(role)) {
      return "organisation admin";
    }
    if (["admin", "Admin"].includes(role)) {
      return "Admin";
    }
    if (["organisation_operator", "OU User", "OU Operator", "Operator", "User"].includes(role)) {
      return "organisation operator";
    }
    return role || "User";
  };

  const currentRole = userProfile?.role || sessionStorage.getItem("userRole") || "";
  const isOrganisationAdmin = [
    "organisation_admin",
    "OU Admin",
  ].includes(currentRole);

  return (
    <>
      <AppBar
        position="static"
        sx={{
          background: "linear-gradient(90deg, #2196F3 0%, #9C27B0 100%)",
          boxShadow: "none",
        }}
      >
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              cursor: "pointer",
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: "'Unbounded', cursive",
                fontWeight: 900,
                color: '#1c1400',
                fontSize: '1.1rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: 1,
              }}
            >
              Aalay<span style={{ color: '#f59e0b', fontSize: '1.4rem' }}>.</span>
            </Typography>

            <Typography
              variant="caption"
              component="div"
              sx={{
                fontStyle: 'italic',
                color: '#1c1400',
                opacity: 0.85,
                fontSize: '0.7rem',
                mt: 0.2,
                fontFamily: 'inherit',
                textTransform: 'none',
              }}
            >
              Intelligent Operations by OmneVu.com
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {tenantInfo?.organisationName || "Organisation"}
            </Typography>
            <Avatar
              src={!logoLoadError ? profileLogoUrl : undefined}
              sx={{ bgcolor: "#673ab7", color: "white", fontWeight: "bold", cursor: "pointer" }}
              onClick={handleMenuOpen}
              imgProps={{
                onError: () => setLogoLoadError(true),
                referrerPolicy: "no-referrer",
              }}
            >
              {getInitials(tenantInfo?.organisationName || userProfile?.adminName || userProfile?.displayName)}
            </Avatar>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  width: "250px",
                  mt: 1.5,
                  borderRadius: "10px",
                  boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5, fontWeight: 500 }}>
                  {userProfile?.email}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Role: {["organisation_admin", "OU Admin"].includes(userProfile?.role)
                    ? "organisation admin"
                    : ["organisation_operator", "OU User", "OU Operator", "Operator", "User"].includes(userProfile?.role)
                      ? "organisation operator"
                      : (userProfile?.role || "User")}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: 250, display: "flex", flexDirection: "column", height: "100%" }}
          role="presentation"
        >
          <List sx={{ flexGrow: 1 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleDrawerNavigation("/")}>
                <ListItemIcon>
                  <img
                    src={logo}
                    alt="OV WMS"
                    style={{ width: 28, height: 28, objectFit: "contain" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Home"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItemButton>
            </ListItem>
            <Divider sx={{ my: 1 }} />
            {isOrganisationAdmin ? (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleDrawerNavigation("/complaints_list")}>
                  <ListItemIcon>
                    <ReportProblemIcon />
                  </ListItemIcon>
                  <ListItemText primary="Complaints" />
                </ListItemButton>
              </ListItem>
            ) : (
              <>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleSectionToggle("warranty")}>
                    <ListItemIcon>
                      <BuildIcon />
                    </ListItemIcon>
                    <ListItemText primary="Warranty Management" />
                    {openSections.warranty ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.warranty} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigation("/warranty_list")}>
                        <ListItemIcon>
                          <CreditCardIcon />
                        </ListItemIcon>
                        <ListItemText primary="Warranty Cards" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigation("/complaints_list")}>
                        <ListItemIcon>
                          <ReportProblemIcon />
                        </ListItemIcon>
                        <ListItemText primary="Complaints" />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>

                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleSectionToggle("amc")}>
                    <ListItemIcon>
                      <ScheduleIcon />
                    </ListItemIcon>
                    <ListItemText primary="AMC Management" />
                    {openSections.amc ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.amc} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigation("/amc/schedule/list")}>
                        <ListItemIcon>
                          <EventNoteIcon />
                        </ListItemIcon>
                        <ListItemText primary="AMC Schedules" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigation("/amc/schedule/new")}>
                        <ListItemIcon>
                          <AddCircleOutlineIcon />
                        </ListItemIcon>
                        <ListItemText primary="New Schedule" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigation("/amc/checklist/user")}>
                        <ListItemIcon>
                          <ChecklistIcon />
                        </ListItemIcon>
                        <ListItemText primary="User Checklist" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigation("/amc/checklist/user/advanced")}>
                        <ListItemIcon>
                          <ChecklistIcon />
                        </ListItemIcon>
                        <ListItemText primary="User Checklist (Advanced)" />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>

                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleSectionToggle("contracts")}>
                    <ListItemIcon>
                      <EventNoteIcon />
                    </ListItemIcon>
                    <ListItemText primary="Contract Management" />
                    {openSections.contracts ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.contracts} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigation("/contracts/list")}>
                        <ListItemIcon>
                          <EventNoteIcon />
                        </ListItemIcon>
                        <ListItemText primary="All Contracts" />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>
              </>
            )}

          </List>

          <Box sx={{ p: 2 }}>
            <Divider />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 1.5,
                display: "block",
                wordBreak: "break-word",
              }}
            >
              Logged in as: {userProfile?.email || auth.currentUser?.email || "N/A"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Role: {getDisplayRole(userProfile?.role)}
            </Typography>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                mt: 1.5,
                backgroundColor: (theme) => `${theme.palette.error.light}40`,
                "&:hover": {
                  backgroundColor: (theme) => `${theme.palette.error.light}60`,
                },
                borderRadius: 1,
              }}
            >
              <ListItemIcon>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontWeight: 600, color: "error.main" }}
              />
            </ListItemButton>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default OrganisationAdminNavBar;
